<script setup lang="ts">
import { EVENTS_ROUTES } from '@/router/routes'
import { EEventActions } from '@/store/eventStore/EventStoreTypes'
import { EStoreModules } from '@/store/storeType'
import {
    advancedEventFiltersKeys,
    defaultAdvancedEventFilters,
    defaultEventFilters,
    type AdvancedEventFilters
} from '@/types/filters/EventFilters'
import type { EventModel } from '@/types/models/EventModel'
import { EEventsPerm } from '@/types/permissions/EventsPermissionsEnum'
import {
    ClickableTypography,
    FullTableLayout,
    HrefCreateButton,
    MobileShowFiltersButton,
    PermissionWrapper,
    TypographyItem
} from '@rfc/rfc-components-library'
import { useTableModel } from '@rfc/rfc-components-library/hooks'
import {
    EProviders,
    type Columns,
    type DateProviderType,
    type SelectorObject,
    type TableEdit
} from '@rfc/rfc-components-library/types'
import { getQueryFilters } from '@rfc/rfc-components-library/utils'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import EventAdvancedFilters from './Table/Filters/EventAdvancedFilters.vue'
import EventTableFilters from './Table/Filters/EventTableFilters.vue'

import { useStore } from 'vuex'

const store = useStore()

const { formatTimestamp } = inject<DateProviderType>(EProviders.DATE, {})

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

type Props = {
    stateAction?: string
    editPermission?: TableEdit
    createPermission?: TableEdit
    disableEdit?: boolean
    propsColumns?: Columns[]
}
const props = defineProps<Props>()

const routeHasAdvancedEventFiltersKeys = Object.keys(route.query).find(item =>
    advancedEventFiltersKeys.includes(item)
)

const columns = computed<Columns[]>(() => [
    { value: 'name', title: t('event.table.eventName') },
    ...(props.propsColumns ?? []),
    { value: 'start_at', title: t('event.table.startDate') },
    { value: 'end_at', title: t('event.table.endDate') }
])

const eventModel = useTableModel<
    EventModel,
    Partial<EventModel> & Partial<AdvancedEventFilters>,
    AdvancedEventFilters
>({
    store,
    stateKey: EStoreModules.EVENT,
    stateAction: props.stateAction ?? EEventActions.FETCH_EVENTS,
    router,
    advancedFiltersQueryKeys: advancedEventFiltersKeys,
    dataFilters: {
        ...defaultEventFilters,
        ...(routeHasAdvancedEventFiltersKeys ? {} : defaultAdvancedEventFilters),
        ...getQueryFilters()
    },
    dataAdvancedFilters: routeHasAdvancedEventFiltersKeys ? {} : defaultAdvancedEventFilters
})

const goToEventCreate = () => props.createPermission?.href ?? router?.push(EVENTS_ROUTES.createUrl)

const filtersTags = computed(() =>
    Object.entries({
        ongoing: eventModel.filters.value?.ongoing,
        upcoming: eventModel.filters.value?.upcoming,
        finished: eventModel.filters.value?.finished
    }).reduce(
        (prev: SelectorObject[], [key, value]) =>
            value ? [...prev, { name: key, value: !!value }] : prev,
        []
    )
)

const actionProps = {
    editPermission: {
        href: props.editPermission?.href,
        permission: props.editPermission?.permission
    }
}
</script>

<template>
    <FullTableLayout
        :advancedFilters="filtersTags"
        :columns="columns"
        :data="eventModel.data.value"
        :loading="eventModel.loading.value"
        :sortBy="eventModel.sort.value"
        :actionProps="actionProps"
        @change:pageNumber="eventModel.handleChangePageNumber"
        @change:pageSize="eventModel.handleChangePageSize"
        @changeSortBy="eventModel.handleChangeSortBy"
        @removeTag="eventModel.handleRemoveTag"
        @search="eventModel.handleSearch"
        translationPath="event.filters"
        class="h-auto">
        <template #filters>
            <MobileShowFiltersButton>
                <EventTableFilters
                    :filters="eventModel.filters.value"
                    @change="eventModel.handleChangeFilters">
                    <slot
                        name="eventFilters"
                        v-bind="{
                            filters: eventModel.filters.value,
                            onChange: eventModel.handleChangeFilters
                        }" />
                </EventTableFilters>
            </MobileShowFiltersButton>
        </template>
        <template #advancedFilters>
            <EventAdvancedFilters
                :filters="eventModel.advancedFilters.value"
                @change="eventModel.handleChangeAdvancedFilters" />
        </template>

        <template #button>
            <PermissionWrapper
                v-if="!disableEdit"
                :permission="createPermission?.permission ?? EEventsPerm.EVENTS_EDIT">
                <HrefCreateButton
                    :href="createPermission?.href ?? EVENTS_ROUTES.createUrl"
                    :label="$t('event.createEvent')"
                    @click="goToEventCreate" />
            </PermissionWrapper>
        </template>

        <template #name="item">
            <TypographyItem v-if="disableEdit" :label="item?.name" />
            <ClickableTypography
                v-else
                :label="item?.name"
                :href="
                    editPermission?.href
                        ? `${editPermission?.href}/${item?.id}`
                        : EVENTS_ROUTES.buildUrl(item?.id)
                " />
        </template>

        <template v-for="(_, name) in $slots" #[name]="item">
            <slot :name="name" v-bind="item as any" />
        </template>

        <template #start_at="item">
            {{ formatTimestamp?.(item?.start_at) }}
        </template>
        <template #end_at="item">
            {{ formatTimestamp?.(item?.end_at) }}
        </template>
    </FullTableLayout>
</template>

<style scoped lang="scss">
.centered-pagination {
    padding: 8px;
}
</style>
